import Client from "fhir-kit-client";
import type { Bundle, Location } from "fhir/r5";

export interface MapLocation {
    id: string;
    reference: string;
    identifiers: string[];
    name: string;
    description?: string;
    organizationReference?: string;
    parentReference?: string;
    level: string;
}

export interface LocationServiceConfig {
    fhirBaseUrl: string;
}

class LocationService {
    private client: Client;

    constructor(config: LocationServiceConfig) {
        this.client = new Client({ baseUrl: config.fhirBaseUrl });
    }

    async searchByManagingOrganizations(
        organizationReferences: string[]
    ): Promise<MapLocation[]> {
        const results = await Promise.all(
            organizationReferences.map((reference) =>
                this.searchByManagingOrganization(reference)
            )
        );

        const locations = results.flat();
        const unique = new Map(locations.map((location) => [location.id, location]));

        return Array.from(unique.values());
    }

    private async searchByManagingOrganization(
        organizationReference: string
    ): Promise<MapLocation[]> {
        const bundle = await this.client.search({
            resourceType: "Location",
            searchParams: {
                organization: organizationReference,
                status: "active",
                _count: 100,
                _sort: "name"
            }
        }) as Bundle<Location>;

        return (
            bundle.entry
                ?.map((entry) => entry.resource)
                .filter(
                    (resource): resource is Location =>
                        !!resource &&
                        resource.resourceType === "Location" &&
                        !!resource.id
                )
                .map((location) => this.mapLocation(location)) ?? []
        );
    }

    private mapLocation(location: Location): MapLocation {
        const id = location.id ?? "";

        return {
            id,
            reference: `Location/${id}`,
            identifiers: location.identifier
                ?.map((identifier) => {
                    if (!identifier.value) return undefined;
                    return `${identifier.system ?? ""}|${identifier.value}`;
                })
                .filter((identifier): identifier is string => !!identifier) ?? [],
            name: location.name ?? id ?? "Lieu",
            description: location.description,
            organizationReference: location.managingOrganization?.reference,
            parentReference: location.partOf?.reference,
            level: this.getLevel(location)
        };
    }

    private getLevel(location: Location): string {
        const levelExtension = location.extension?.find((extension) => {
            const url = extension.url.toLowerCase();
            return url.endsWith("level") || url.endsWith("floor") || url.includes("etage");
        });

        const value = levelExtension?.valueString ??
            levelExtension?.valueCode ??
            levelExtension?.valueInteger;

        return value === undefined || value === null ? "0" : String(value);
    }
}

export default LocationService;
